/**
 * Вітальна сторінка для всіх користувачів
 * @component WelcomePage
 * @file WelcomePage.tsx
 * @path frontend/src/pages/WelcomePage.tsx
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useNavigation } from '../../hooks/useNavigation';
import './style.css';

// Welcome section component
const WelcomeSection: React.FC<{ user: any; language: string; onClose: () => void }> = ({ user, language, onClose }) => {
  return (
    <div className="hero-layout">
      <div className="container">
        <div className="hero-section">
          <button 
            className="hero-close-btn"
            onClick={onClose}
            title={language === 'ua' ? 'Закрити' : 'Close'}
          >
            <i className="bi bi-x"></i>
          </button>
          
          {/* <h1 className="page-title">
            {language === 'ua' 
              ? 'Ласкаво просимо до платформи OnVisit' 
              : 'Welcome to OnVisit Platform'
            }
          </h1> */}
          
          <p className="hero-description">
            {language === 'ua'
              ? user?.role === 'client' 
                ? 'Зручна система запису до салонів краси та управління вашими візитами'
                : 'Професійна система управління салонами краси, записами клієнтів та послугами'
              : user?.role === 'client'
                ? 'Convenient beauty salon booking system and visit management'
                : 'Professional beauty salon management system for clients, appointments and services'
            }
          </p>

          <div className="welcome-back">
            <h2>
              {language === 'ua' 
                ? `Вітаємо, ${user?.first_name}!`
                : `Welcome back, ${user?.first_name}!`
              }
            </h2>
            <p>
              {language === 'ua'
                ? `Ваша роль: ${user?.role}`
                : `Your role: ${user?.role}`
              }
            </p>
          </div>
        </div>

        <div className="features-section">
          <h2>
            {language === 'ua' ? 'Можливості платформи' : 'Platform Features'}
          </h2>
          
          <div className="features-grid">
            {user?.role === 'client' ? (
              <>
                <div className="feature-card">
                  <i className="bi bi-calendar-check"></i>
                  <h3>
                    {language === 'ua' ? 'Запис до майстрів' : 'Book with Masters'}
                  </h3>
                  <p>
                    {language === 'ua'
                      ? 'Легко записуйтесь на послуги до найкращих майстрів'
                      : 'Easily book services with the best masters'
                    }
                  </p>
                </div>

                <div className="feature-card">
                  <i className="bi bi-heart"></i>
                  <h3>
                    {language === 'ua' ? 'Улюблені майстри' : 'Favorite Masters'}
                  </h3>
                  <p>
                    {language === 'ua'
                      ? 'Зберігайте улюблених майстрів для швидкого доступу'
                      : 'Save your favorite masters for quick access'
                    }
                  </p>
                </div>

                <div className="feature-card">
                  <i className="bi bi-star"></i>
                  <h3>
                    {language === 'ua' ? 'Відгуки та рейтинги' : 'Reviews & Ratings'}
                  </h3>
                  <p>
                    {language === 'ua'
                      ? 'Діліться враженнями та допомагайте іншим клієнтам'
                      : 'Share your experience and help other clients'
                    }
                  </p>
                </div>
              </>
            ) : (
              <>
                <div className="feature-card">
                  <i className="bi bi-calendar-check"></i>
                  <h3>
                    {language === 'ua' ? 'Запис клієнтів' : 'Client Appointments'}
                  </h3>
                  <p>
                    {language === 'ua'
                      ? 'Зручна система запису та управління розкладом'
                      : 'Convenient booking and schedule management system'
                    }
                  </p>
                </div>

                <div className="feature-card">
                  <i className="bi bi-person-workspace"></i>
                  <h3>
                    {language === 'ua' ? 'Управління майстрами' : 'Master Management'}
                  </h3>
                  <p>
                    {language === 'ua'
                      ? 'Керування командою майстрів та їх спеціалізаціями'
                      : 'Manage your team of masters and their specializations'
                    }
                  </p>
                </div>

                <div className="feature-card">
                  <i className="bi bi-clock"></i>
                  <h3>
                    {language === 'ua' ? 'Графік роботи' : 'Work Schedule'}
                  </h3>
                  <p>
                    {language === 'ua'
                      ? 'Налаштуйте графік роботи та прийом клієнтів'
                      : 'Configure work schedule and client appointments'
                    }
                  </p>
                </div>

                <div className="feature-card">
                  <i className="bi bi-speedometer2"></i>
                  <h3>
                    {language === 'ua' ? 'Панель керування' : 'Dashboard'}
                  </h3>
                  <p>
                    {language === 'ua'
                      ? 'Статистика та аналітика вашого бізнесу'
                      : 'Statistics and analytics of your business'
                    }
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const WelcomePage: React.FC = () => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const { navigateToPrivate } = useNavigation();
  const [showWelcomeSection, setShowWelcomeSection] = useState(false);

  // Check if it's user's first visit
  useEffect(() => {
    if (user) {
      const hasSeenWelcome = localStorage.getItem(`welcome_seen_${user.id}`);
      if (!hasSeenWelcome) {
        // First visit - show welcome and save this fact
        localStorage.setItem(`welcome_seen_${user.id}`, 'true');
        setShowWelcomeSection(true);
      }
    }
  }, [user]);

  const handleCloseWelcome = () => {
    setShowWelcomeSection(false);
  };

  if (!user) {
    return <div>Loading user data...</div>;
  }

  return (
    <>
      {showWelcomeSection && (
        <WelcomeSection 
          user={user} 
          language={language} 
          onClose={handleCloseWelcome}
        />
      )}
      
      {/* Always show the main welcome page content */}
      <div className="welcome-page">
        <div className="container">
          <div className="welcome-content">
            {/* <h1 className="page-title">
              {language === 'ua' 
                ? 'Ласкаво просимо до платформи OnVisit' 
                : 'Welcome to OnVisit Platform'
              }
            </h1> */}
            
            {/* <div className="user-info">
              <h2>
                {language === 'ua' 
                  ? `Вітаємо, ${user?.first_name}!`
                  : `Welcome back, ${user?.first_name}!`
                }
              </h2>
              <p>
                {language === 'ua'
                  ? `Ваша роль: ${user?.role}`
                  : `Your role: ${user?.role}`
                }
              </p>
            </div> */}

            <div className="description">
              <p>
                {language === 'ua' 
                  ? 'Оберіть одну з функцій нижче, щоб почати роботу з платформою. Ви можете завжди повернутися на цю сторінку через меню HOME.'
                  : 'Choose one of the functions below to start working with the platform. You can always return to this page through the HOME menu.'
                }
              </p>
            </div>

            <div className="actions">
              {/* <button 
                className="primary-button"
                onClick={handleGoToWork}
              >
                <i className="bi bi-arrow-right"></i>
                {language === 'ua' ? 'Перейти до роботи' : 'Go to Work'}
              </button> */}
            </div>

            <div className="features-section">
              <h3>
                {language === 'ua' ? 'Швидкий доступ' : 'Quick Access'}
              </h3>
              
              <div className="quick-actions">
                {user?.role === 'client' ? (
                  <>
                    <button 
                      className="action-card"
                      onClick={() => navigateToPrivate('MY_RECORDS')}
                    >
                      <i className="bi bi-calendar-check"></i>
                      <span>{language === 'ua' ? 'Мої записи' : 'My Records'}</span>
                    </button>
                    <button 
                      className="action-card"
                      onClick={() => navigateToPrivate('FAVORITE_MASTERS')}
                    >
                      <i className="bi bi-heart"></i>
                      <span>{language === 'ua' ? 'Улюблені майстри' : 'Favorite Masters'}</span>
                    </button>
                    <button 
                      className="action-card"
                      onClick={() => navigateToPrivate('REVIEWS')}
                    >
                      <i className="bi bi-star"></i>
                      <span>{language === 'ua' ? 'Відгуки' : 'Reviews'}</span>
                    </button>
                  </>
                ) : (
                  <>
                    <button 
                      className="action-card"
                      onClick={() => navigateToPrivate('SETTINGS_BUSINESS')}
                    >
                      <i className="bi bi-credit-card"></i>
                      <span>{language === 'ua' ? 'Налаштування картки бізнесу' : 'Business Card Settings'}</span>
                    </button>
                    <button 
                      className="action-card"
                      onClick={() => navigateToPrivate('SETTINGS_SCHEDULE')}
                    >
                      <i className="bi bi-clock"></i>
                      <span>{language === 'ua' ? 'Налаштування графіка' : 'Schedule Settings'}</span>
                    </button>
                    <button 
                      className="action-card"
                      onClick={() => navigateToPrivate('CLIENTS')}
                    >
                      <i className="bi bi-people"></i>
                      <span>{language === 'ua' ? 'Клієнти' : 'Clients'}</span>
                    </button>
                    <button 
                      className="action-card"
                      onClick={() => navigateToPrivate('APPOINTMENTS')}
                    >
                      <i className="bi bi-calendar-check"></i>
                      <span>{language === 'ua' ? 'Записи' : 'Appointments'}</span>
                    </button>
                    <button 
                      className="action-card disabled"
                      disabled
                      title={language === 'ua' ? 'Панель керування тимчасово недоступна' : 'Dashboard temporarily unavailable'}
                    >
                      <i className="bi bi-speedometer2"></i>
                      <span>{language === 'ua' ? 'Панель керування (в розробці)' : 'Dashboard (in development)'}</span>
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default WelcomePage;
